import { FC, memo, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AdminProtected } from "@/app/application/AdminProtected";
import { SATOSLink } from "@/app/application/SATOSLink";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	getCardBorderStyle,
	getSurfaceBorderColor,
} from "@/app/application/theme";
import { UserContext, TranslationContext } from "@/app/providers";
import { useGetUpdatedProcedures } from "@/features/procedures/api/procedure.queries";
import { UppdatedProcedureVersion } from "@/features/procedures/model/types/updatedProcedureVersion";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Skeleton,
	Stack,
	TextField,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { Language } from "@/model/Language/Language";


/* ---------- Small helpers (inline) ---------- */

const LeftAccent: FC<{ color?: string }> = ({ color }) => {
	const theme = useTheme();
	const accentColor =
		color ??
		(theme.palette.mode === "dark" ? theme.palette.warning.main : "#F5D565");
	return (
		<Box
			sx={{
				width: 4,
				minWidth: 4,
				alignSelf: "stretch",
				borderRadius: 1,
				bgcolor: accentColor,
				mr: 1.5,
			}}
		/>
	);
};

const InfoChip: FC<{ label: React.ReactNode }> = ({ label }) => (
	<Chip
		size="small"
		variant="outlined"
		label={label}
		sx={{
			borderRadius: 1.5,
			borderColor: "divider",
			"& .MuiChip-label": { fontSize: "0.75rem" },
		}}
	/>
);

/* ---------- Item (row-like card) ---------- */

interface iProcedureItem {
	procedure: UppdatedProcedureVersion;
}

const ProcedureItem: FC<iProcedureItem> = memo(({ procedure }) => {
	const navigate = useNavigate();
	const goToCompare = () =>
		navigate(
			`/procedure/${procedure.ProcedureId}/node/${procedure.NodeSchemeId}/CompareVersions`,
		);

	return (
		<Grid item xs={12} sm={6} lg={4}>
			<Card
				variant="outlined"
				sx={(t) => ({
					height: "100%",
					borderRadius: 2,
					border: getCardBorderStyle(t),
					boxShadow:
						t.palette.mode === "dark"
							? "0 1px 0 0 rgba(255,255,255,0.05)"
							: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
					transition:
						"transform .2s ease, box-shadow .2s ease, border-color .2s ease",
					"&:hover": {
						transform: "translateY(-2px)",
						boxShadow:
							t.palette.mode === "dark"
								? "0 4px 20px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.06)"
								: "0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px " +
									alpha(t.palette.primary.main, 0.25),
						borderColor: alpha(t.palette.primary.main, 0.35),
					},
				})}
			>
				<CardActionArea onClick={goToCompare} sx={{ height: "100%" }}>
					<CardContent sx={{ p: 2.25 }}>
						<Stack direction="row" spacing={1.5} alignItems="flex-start">
							<LeftAccent />
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									variant="h6"
									component="h2"
									sx={{ fontWeight: 700 }}
									noWrap
								>
									<SATOSLink
										to="AccessProcedure"
										params={[procedure.ProcedureId]}
									>
										{procedure.ProcedureName}
									</SATOSLink>
								</Typography>

								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									flexWrap="wrap"
									useFlexGap
									sx={{ mt: 0.5 }}
								>
									<InfoChip
										label={
											<Typography variant="caption">
												<Translate String="Procedures.UpdateList.Version">
													Version:
												</Translate>{" "}
												{procedure.NewRevision}
											</Typography>
										}
									/>
									<InfoChip
										label={
											<Typography variant="caption" noWrap>
												<Translate String="Procedures.UpdateList.Node">
													Node:
												</Translate>{" "}
												{procedure.NodeName}
											</Typography>
										}
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ ml: "auto" }}
										title={dayjs(procedure.Updated).format()}
									>
										<Translate String="Procedures.UpdateList.Updated">
											Updated:
										</Translate>{" "}
										{dayjs(procedure.Updated).format("DD/MM/YY")}
									</Typography>
								</Stack>
							</Box>

							<IconButton size="small" sx={{ ml: 0.5 }}>
								<ChevronRightIcon fontSize="small" />
							</IconButton>
						</Stack>

						<Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								startIcon={<PreviewIcon fontSize="small" />}
								component={Link}
								to={`/procedure/${procedure.ProcedureId}/node/${procedure.NodeSchemeId}/CompareVersions`}
								sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
								onClick={(e) => e.stopPropagation()}
							>
								<Translate String="Procedures.UpdateList.Review.Changes">
									Review changes
								</Translate>
							</Button>
						</Stack>
					</CardContent>
				</CardActionArea>
			</Card>
		</Grid>
	);
});
ProcedureItem.displayName = "ProcedureItem";

/* ---------- Page ---------- */

export const ShowUpdatedProcedures: FC = () => {
	const translation: Language | null = useContext(TranslationContext);
	const user = useContext(UserContext);
	const [companyId, setCompanyId] = useState<number>();
	const { data, isLoading } = useGetUpdatedProcedures(companyId);
	const [searchQuery, setSearchQuery] = useState("");

	useEffect(() => {
		if (user?.CompanyId) setCompanyId(user.CompanyId);
	}, [user]);

	const filtered = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		const list = data ?? [];
		return q
			? list.filter((n) => n.ProcedureName.toLowerCase().includes(q))
			: list;
	}, [data, searchQuery]);

	return (
		<AdminProtected>
			<Box
				className="procedure-compare-page"
				sx={{
					height: "100%",
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: "background.default",
				}}
			>
				<Box
					sx={{
						p: { xs: 2, sm: 2.5 },
						mx: "auto",
						width: "100%",
						maxWidth: 1400,
						flex: "1 1 0%",
						minHeight: 0,
						overflow: "auto",
					}}
				>
					{/* Header strip with title + search (aligned with ProcedureUpdates / checklist editor) */}
					<Paper
						elevation={0}
						variant="outlined"
						sx={(t) => ({
							borderRadius: 2,
							border: getCardBorderStyle(t),
							boxShadow:
								t.palette.mode === "dark"
									? "0 1px 0 0 rgba(255,255,255,0.05)"
									: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
							overflow: "hidden",
							mb: 2,
						})}
					>
						<Box
							sx={(t) => ({
								px: 2,
								pt: 2,
								pb: 2,
								borderBottom: 1,
								borderColor: "divider",
								bgcolor: t.palette.mode === "dark" ? "action.hover" : "grey.50",
								borderRadius: "12px 12px 0 0",
							})}
						>
							<Typography
								variant="h6"
								component="h1"
								sx={{ fontWeight: 600, fontSize: "1rem", mb: 1.5 }}
							>
								<Translate String="Procedures.UpdateList.Header">
									Procedures with updated nodes
								</Translate>
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											fontWeight: 600,
											fontSize: "0.8125rem",
											color: "text.secondary",
											mb: 1,
										}}
									>
										<Translate String="Procedures.UpdateList.Search">
											Search Procedures
										</Translate>
									</Typography>
									<TextField
										fullWidth
										placeholder={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Pages.UpdatedProceduresPage.UpdatedProceduresPage.L297C23.SearchProcedures", "Search procedures…")}
										variant="outlined"
										size="small"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon
														fontSize="small"
														sx={{ color: "text.secondary" }}
													/>
												</InputAdornment>
											),
											endAdornment: searchQuery ? (
												<InputAdornment position="end">
													<IconButton
														size="small"
														aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Pages.UpdatedProceduresPage.UpdatedProceduresPage.L315C26.ClearSearch", "Clear search")}
														onClick={() => setSearchQuery("")}
														edge="end"
													>
														<ClearIcon fontSize="small" />
													</IconButton>
												</InputAdornment>
											) : null,
										}}
										sx={{
											"& .MuiOutlinedInput-root": {
												borderRadius: 2,
												bgcolor: "background.paper",
												"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
													borderColor: (t: {
														palette: { primary: { main: string } };
													}) => alpha(t.palette.primary.main, 0.5),
												},
											},
										}}
									/>
								</Grid>
							</Grid>
						</Box>
					</Paper>

					{/* List */}
					<Grid container spacing={2}>
						{isLoading ? (
							Array.from({ length: 6 }).map((_, i) => (
								<Grid item xs={12} sm={6} lg={4} key={`skeleton-${i}`}>
									<Card
									variant="outlined"
									sx={(t) => ({
										borderRadius: 2,
										p: 2.25,
										border: getCardBorderStyle(t),
										boxShadow:
												t.palette.mode === "dark"
													? "0 1px 0 0 rgba(255,255,255,0.05)"
													: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
										})}
									>
										<Skeleton
											variant="text"
											height={28}
											width="80%"
											sx={{ mb: 1 }}
										/>
										<Skeleton variant="text" height={18} width="60%" />
										<Skeleton
											variant="rounded"
											height={36}
											width="40%"
											sx={{ mt: 2 }}
										/>
									</Card>
								</Grid>
							))
						) : filtered.length > 0 ? (
							filtered.map((node) => (
								<ProcedureItem
									key={`${node.ProcedureId}-${node.NodeSchemeId}-${node.NewRevision}`}
									procedure={node}
								/>
							))
						) : (
							<Grid item xs={12} textAlign="center">
								<Paper
									elevation={0}
									variant="outlined"
									sx={(t) => ({
										borderRadius: 2,
										p: 6,
										border: `1px dashed ${getSurfaceBorderColor(t, "strong")}`,
										backgroundColor:
											t.palette.mode === "dark"
												? alpha(t.palette.background.paper, 0.6)
												: alpha(t.palette.primary.main, 0.04),
									})}
								>
									<Typography
										variant="h6"
										component="h3"
										color="text.secondary"
										sx={{ mb: 1, fontWeight: 600 }}
									>
										<Translate String="Procedures.UpdateList.Empty">
											No updated nodes
										</Translate>
									</Typography>
									<Typography variant="body2" color="text.secondary">
										<Translate String="Procedures.UpdateList.EmptyHint">
											Try adjusting your search or check back later.
										</Translate>
									</Typography>
								</Paper>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>
		</AdminProtected>
	);
};

export default ShowUpdatedProcedures;
